import { Input } from 'antd';
import { memo, useCallback, useState } from 'react';
import { ICheckoutItem } from '../../../interfaces/ICheckout';
import { useForm } from '../context';
import { getFrequency, moneyMask } from '../helper';

const MAX_CENTS = 99999999;

type OpenValueInputProps = {
  firstItem: ICheckoutItem;
  minAmount?: number;
  maxAmount?: number;
  isRecurrence: boolean;
};

const OpenValueInput = memo(({ firstItem, minAmount, maxAmount, isRecurrence }: OpenValueInputProps) => {
  const { updateItemAmount, getTotal, items } = useForm();
  const enrollmentFee = items.find((item) => item.type === 'enrollment_fee');
  const orderBumpItems = items.filter((item) => item.type === 'order_bump');
  const orderBumpTotal = orderBumpItems.reduce((acc, item) => acc + (item?.couponItem?.amount ?? item.amount) * item.quantity, 0);

  const [openValueDisplay, setOpenValueDisplay] = useState('');
  const [openValueError, setOpenValueError] = useState('');

  const applyCents = useCallback(
    (amountInCents: number, input?: HTMLInputElement) => {
      const capped = Math.max(0, Math.min(amountInCents, MAX_CENTS));
      const cappedByMax = maxAmount != null ? Math.min(capped, maxAmount) : capped;
      const formatted = moneyMask.apply(cappedByMax / 100);
      setOpenValueDisplay(formatted);

      if (cappedByMax <= 0) {
        setOpenValueError('Informe um valor válido');
        updateItemAmount(firstItem.id, 0);
      } else if (minAmount != null && cappedByMax < minAmount) {
        setOpenValueError(`Valor mínimo: R$ ${moneyMask.apply(minAmount / 100)}`);
        updateItemAmount(firstItem.id, 0);
      } else if (maxAmount != null && cappedByMax > maxAmount) {
        setOpenValueError(`Valor máximo: R$ ${moneyMask.apply(maxAmount / 100)}`);
        updateItemAmount(firstItem.id, 0);
      } else {
        setOpenValueError('');
        updateItemAmount(firstItem.id, cappedByMax);
      }

      requestAnimationFrame(() => {
        if (input) input.setSelectionRange(formatted.length, formatted.length);
      });
    },
    [firstItem.id, minAmount, maxAmount, updateItemAmount]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.target;
      const currentCents = Math.round(moneyMask.clean(openValueDisplay || '0') * 100);

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        const digit = parseInt(e.key, 10);
        applyCents(currentCents * 10 + digit, input as HTMLInputElement);
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        applyCents(Math.floor(currentCents / 10), input as HTMLInputElement);
      }
    },
    [openValueDisplay, applyCents]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const raw = (e.clipboardData.getData('text') || '').replace(/[^\d,]/g, '');
      const amountInCents = Math.round((moneyMask.clean(raw) || 0) * 100);
      applyCents(amountInCents, e.currentTarget);
    },
    [applyCents]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const raw = input.value.replace(/[^\d,]/g, '');
      const amountInCents = Math.round((moneyMask.clean(raw) || 0) * 100);
      applyCents(amountInCents, input);
    },
    [applyCents]
  );

  const inputBlock = (
    <>
      <label className="block text-sm mb-1">
        {isRecurrence ? 'Informe o valor por ciclo:' : 'Informe o valor:'}
      </label>
      <Input
        prefix="R$"
        size="large"
        placeholder="0,00"
        value={openValueDisplay}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        status={openValueError ? 'error' : undefined}
        className="max-w-48"
      />
      {openValueError && <p className="text-red-500 text-xs mt-1">{openValueError}</p>}
      {!openValueError && minAmount && (
        <p className="text-gray-400 text-xs mt-1">Mínimo: R$ {moneyMask.apply(minAmount / 100)}</p>
      )}
    </>
  );

  if (!isRecurrence) {
    return (
      <div className="text-gray-600">
        {inputBlock}
        {orderBumpTotal > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            Total: R$ {moneyMask.apply(getTotal() / 100)}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="text-gray-600">
      {enrollmentFee && (
        <div className="mb-2">
          <strong className="font-medium">{enrollmentFee.description}</strong>:{' '}
          <span className="text-lg font-medium text-black">
            R$ {moneyMask.apply(getTotal() / 100)} à vista.
          </span>
        </div>
      )}

      <h2 className="text-base font-medium">Assinatura</h2>
      {inputBlock}

      <div className="text-sm text-gray-500 mt-1">
        a cada {firstItem?.recurrenceInterval}{' '}
        {getFrequency(firstItem?.recurrenceFrequency, firstItem?.recurrenceInterval)}
        {firstItem?.recurrenceChargesQuantity != null && firstItem.recurrenceChargesQuantity > 0 && (
          <>
            {' '}
            durante o período de {firstItem.recurrenceInterval * firstItem.recurrenceChargesQuantity}{' '}
            {getFrequency(
              firstItem?.recurrenceFrequency,
              firstItem.recurrenceInterval * firstItem.recurrenceChargesQuantity
            )}
            .
          </>
        )}
      </div>

      {orderBumpTotal > 0 && (
        <p className="text-sm text-gray-500 mt-2">
          Total: R$ {moneyMask.apply(getTotal() / 100)}
        </p>
      )}
    </div>
  );
});

OpenValueInput.displayName = 'OpenValueInput';

export default OpenValueInput;
